import { useMemo } from 'react';
import { pluralize } from 'utils/shared/pluralize';
import { useModal } from 'hooks/useModal';
import Star from 'svg/Star';
import Button from 'components/Button';
import LoadingSkeleton from 'components/LoadingSkeleton';
import ReviewCard from 'screens/CoachDetails/components/ReviewCard';
import ReviewsOverviewModal from 'screens/CoachDetails/components/ReviewsOverviewModal';
import SectionDivider from 'screens/ClubOverview/components/SectionDivider';
import { useVenueReviews } from './useVenueReviews';

const VISIBLE_REVIEWS = 3;

interface Props {
  venueId: string;
}

export default function VenueReviews({ venueId }: Props) {
  const { reviews, isLoading } = useVenueReviews({ venueId });
  const reviewsOverviewModal = useModal();

  const withReviews = useMemo(() => reviews.filter((r) => r.review).length, [reviews]);
  const generalRating = useMemo(
    () =>
      reviews.length > 0
        ? (reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length).toFixed(1)
        : '0.0',
    [reviews],
  );

  return (
    <>
      <SectionDivider className="my-ds-3xl" />
      <section className="flex flex-col gap-ds-xl">
        <div className="flex flex-col gap-ds-sm">
          <div className="flex items-center gap-ds-lg">
            <h2 className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              Reviews ({reviews.length})
            </h2>
            {reviews.length > 0 && (
              <div className="flex items-center gap-1">
                <Star className="mb-1 h-8 w-8 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary" />
                <span className="typography-product-heading text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
                  {generalRating}
                </span>
              </div>
            )}
          </div>
          <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            {`${reviews.length} total ${pluralize({
              singular: 'rating',
              plural: 'ratings',
              count: reviews.length,
              hideCount: true,
            })}, ${withReviews} with ${pluralize({
              singular: 'review',
              plural: 'reviews',
              count: withReviews,
              hideCount: true,
            })}`}
          </p>
        </div>
        {isLoading ? (
          <LoadingSkeleton count={3} height="8rem" containerClassName="flex flex-col gap-ds-2xl" />
        ) : (
          reviews.length > 0 && (
            <div className="flex flex-col gap-ds-3xl">
              {reviews.slice(0, VISIBLE_REVIEWS).map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          )
        )}
        {reviews.length > VISIBLE_REVIEWS && (
          <div className="flex">
            <Button
              size="md"
              variant="secondary"
              isInline
              onClick={() => reviewsOverviewModal.openModal()}
            >
              View all reviews
            </Button>
          </div>
        )}
      </section>
      <ReviewsOverviewModal
        open={reviewsOverviewModal.isOpen}
        handleClose={reviewsOverviewModal.closeModal}
        reviews={reviews}
      />
    </>
  );
}
